const DEMO_COOKIE_NAME = "calcera_demo_session";

type DemoIdentity = {
  userId?: string | null;
  email?: string | null;
};

function getConfiguredDemoIdentity(): Required<DemoIdentity> {
  // Clerk user ids look like "user_xxx". We use the backend seed default as a fallback so local dev works out of the box.
  const envUserId =
    ((import.meta as any)?.env?.VITE_DEMO_USER_ID as string | undefined) ||
    "user_38WPOnsRTK8SNyHARROaT6e1cJ8";
  const envEmail =
    ((import.meta as any)?.env?.VITE_DEMO_EMAIL as string | undefined) ||
    "calcerademo@gmail.com";
  return { userId: envUserId, email: envEmail };
}

function isDemoUser(identity?: DemoIdentity): boolean {
  if (!identity) return false;
  const { userId: demoUserId, email: demoEmail } = getConfiguredDemoIdentity();
  const uid = typeof identity.userId === "string" ? identity.userId.trim() : "";
  const email = typeof identity.email === "string" ? identity.email.trim() : "";
  return Boolean((uid && uid === demoUserId) || (email && email.toLowerCase() === demoEmail.toLowerCase()));
}

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    // ignore
  }
  return `demo-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function setCookie(name: string, value: string, opts?: { maxAgeSeconds?: number }) {
  if (typeof document === "undefined") return;
  const maxAge = typeof opts?.maxAgeSeconds === "number" ? opts.maxAgeSeconds : undefined;
  const parts = [`${encodeURIComponent(name)}=${encodeURIComponent(value)}`, "Path=/", "SameSite=Lax"];
  if (maxAge != null) parts.push(`Max-Age=${Math.max(0, Math.floor(maxAge))}`);
  // Note: do not set Secure here; local dev often runs over http.
  document.cookie = parts.join("; ");
}

function deleteCookie(name: string) {
  if (typeof document === "undefined") return;
  document.cookie = `${encodeURIComponent(name)}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const target = `${encodeURIComponent(name)}=`;
  const cookies = String(document.cookie || "")
    .split(";")
    .map((c) => c.trim());
  for (const c of cookies) {
    if (!c.startsWith(target)) continue;
    const raw = c.slice(target.length);
    try {
      return decodeURIComponent(raw);
    } catch {
      return raw;
    }
  }
  return null;
}

export function enterDemoMode(): string {
  const existing = getDemoSessionId();
  const sid = existing || randomId();
  // Keep the cookie around for demos; clearing cookies resets the demo state.
  setCookie(DEMO_COOKIE_NAME, sid, { maxAgeSeconds: 60 * 60 * 24 * 7 });
  return sid;
}

export function getDemoSessionId(): string | null {
  const v = readCookie(DEMO_COOKIE_NAME);
  const s = typeof v === "string" ? v.trim() : "";
  return s ? s : null;
}

export function isDemoMode(): boolean {
  return Boolean(getDemoSessionId());
}

/**
 * Demo mode is entered via the Playground button, which sets a cookie.
 *
 * IMPORTANT: We scope demo mode to the demo user so the cookie cannot accidentally
 * disable chat/features for normal dev accounts signing in on the same browser.
 *
 * - If identity is provided and it is NOT the demo user, we auto-clear the cookie.
 * - If identity is not provided (e.g., before auth finishes), we fall back to cookie presence.
 */
export function isDemoModeScoped(identity?: DemoIdentity): boolean {
  const sid = getDemoSessionId();
  if (!sid) return false;

  // If we know who the user is and it's not the demo user, self-heal by clearing the cookie.
  if (identity?.userId || identity?.email) {
    if (!isDemoUser(identity)) {
      clearDemoMode();
      return false;
    }
    return true;
  }

  // Unknown auth state: keep legacy behavior so the demo sign-in flow can gate early.
  return true;
}

export function clearDemoMode(): void {
  deleteCookie(DEMO_COOKIE_NAME);
}

export function demoStorageKey(kind: "notes" | "tasks" | "timesheets", suffix?: string): string | null {
  const sid = getDemoSessionId();
  if (!sid) return null;
  const base = `calcera:demo:${sid}:${kind}`;
  return suffix ? `${base}:${suffix}:snapshot` : `${base}:snapshot`;
}


